import React, { useEffect, useState } from "react";
import "../../css/CRUDModal.css";
import ButtonTriangle from "../../assets/Icons/bthTriangle.svg";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { useForm, Controller } from "react-hook-form";
import { Box, CircularProgress } from "@mui/material";
import CustomAlert from "../../helpers/CustomAlert";
import { axiosGetApi, axiosPostApi } from "../../helpers/interceptorServices";
import { apiList } from "../../helpers/apiList";
import { format, parse } from "date-fns";
import { ReactComponent as ChevronDownIcon } from "../../assets/Icons/Chevron down.svg";
import { ReactComponent as ChevronUp } from "../../assets/Icons/ChevronUp.svg";

const statusOptions = [
  { value: "DELIVERED", label: "Delivered" },
  { value: "PENDING", label: "Pending" },
  { value: "FAILED", label: "Failed" },
  { value: "EXCEPTION", label: "Exception" },
  { value: "SEND", label: "Send" },
  { value: "NOT REACHABLE", label: "Not Reachable" },
];

const operatorOptions = [
  { value: "smartping", label: "Smartping" },
  { value: "jio", label: "Jio" },
  { value: "onextel", label: "Onextel" },
];

const SMSHistoryFilterModal = ({
  show,
  handleClose,
  setFilterData,
  filterData,
  handleReset,
  setHistoryData,
  setPageDetails,
  setNoData,
  pageSize,
  currentPage,
}) => {
  const userRole = sessionStorage.getItem("userRole");
  const isCust = userRole === "CUST";

  // Define available tabs based on userRole
  const availableTabs = isCust
    ? ["Mobile No", "Status", "Created Date"]
    : [
        userRole === "CUST" ? "Company ID" : "Company Name",
        "Mobile No",
        "Status",
        "Created Date",
        "Operator",
      ];

  // Set default selected tab, ensuring it's valid for CUST users
  const [selectedTab, setSelectedTab] = useState(
    isCust ? "Mobile No" : userRole === "CUST" ? "Company ID" : "Company Name",
  );

  const {
    handleSubmit,
    register,
    setValue,
    reset,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: {
      userCode: filterData.userCode || "",
      mobileNo: filterData.mobileNo || "",
      status: filterData.status || [],
      operator: filterData.operator || [],
      fromDate: filterData.fromDate || getTodayDate(),
      toDate: filterData.toDate || getTodayDate(),
    },
    mode: "onChange",
  });

  const [loading, setLoading] = useState(false);
  const [customerList, setCustomerList] = useState([]);
  const [customerNameOpen, setCustomerNameOpen] = useState(false);

  const loggedInUserName = sessionStorage.getItem("username");

  const handleTabChange = (e, tabName) => {
    e.preventDefault();
    setSelectedTab(tabName);
  };

  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const [year, month, day] = dateStr.split("-");
    return `${day}-${month}-${year}`;
  };

  const onSubmit = (data) => {
    setFilterData(data);
    fetchSMSHistoryData(data, currentPage);
  };

  const fetchSMSHistoryData = async (data, pageNo) => {
    setLoading(true);
    try {
      const requestBody = {
        username: loggedInUserName,
        companyCode: data.userCode || "",
        mobileNo: data.mobileNo || "",
        status: data.status || [],
        responseTxnId: "",
        rrn: "",
        transactionType: "",
        vendorType: "",
        fromDate: formatDate(data.fromDate),
        toDate: formatDate(data.toDate),
        otp: data.operator || [],
      };

      const response = await axiosPostApi(
        `${apiList.FETCH_SMS_MASTER}?pageSize=${pageSize}&pageNo=${pageNo}`,
        requestBody,
      );

      if (
        response?.data?.status === true &&
        response?.data?.respCode === "00"
      ) {
        setPageDetails(response?.data?.pageDetails);
        const modifiedData = modifyStructure(response?.data?.data);
        setHistoryData(modifiedData);
        setNoData(modifiedData?.length === 0);
        handleClose();
      } else {
        CustomAlert({
          title: "Failed",
          text:
            response?.data?.message ||
            "There was an issue processing the request.",
          icon: "error",
          confirmButtonText: "Retry",
        });

        // setHistoryData([]);
        // setNoData(true);
      }
    } catch (error) {
      CustomAlert({
        title: "Error",
        text:
          error.response?.data?.message ||
          "An error occurred while fetching the SMS history data. Please try again later.",
        icon: "error",
        confirmButtonText: "Retry",
      });
      setHistoryData([]);
      setNoData(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomerData();
  }, []);

  const fetchCustomerData = async () => {
    try {
      const response = await axiosGetApi(apiList.ALL_ACTIVE_CUSTOMER_DATA);

      if (response?.status === 200) {
        setCustomerList(response?.data?.data);
      }
    } catch (error) {
      console.error("Error fetching customer data:", error);
    }
  };

  const toggleCustomerNameDropdown = () =>
    setCustomerNameOpen(!customerNameOpen);

  const modifyStructure = (response) => {
    const modified = response?.map((record, index) => {
      const otpDateParsed = record?.otpDate
        ? parse(record.otpDate, "dd-MM-yyyy HH:mm:ss", new Date())
        : null;

      const createdDateParsed = record?.createdDate
        ? parse(record.createdDate, "dd-MM-yyyy HH:mm:ss", new Date())
        : null;

      const respDateParsed = record?.responseDate
              ? parse(record.responseDate, "dd-MM-yyyy HH:mm:ss", new Date())
              : null;

      return {
        ...record,
        id: index + 1,
        otpDate: otpDateParsed
          ? [
              format(otpDateParsed, "dd-MM-yyyy"),
              <br key={index} />,
              format(otpDateParsed, "HH:mm:ss"),
            ]
          : "",
        createdDate: createdDateParsed
          ? [
              format(createdDateParsed, "dd-MM-yyyy"),
              <br key={index} />,
              format(createdDateParsed, "HH:mm:ss"),
            ]
          : "",

          responseDate: respDateParsed
                    ? [
                        format(respDateParsed, "dd-MM-yyyy"),
                        <br key={index} />,
                        format(respDateParsed, "HH:mm:ss"),
                      ]
                    : "",
      };
    });

    return modified;
  };

  return (
    <>
      {show && (
        <div
          style={{
            position: "fixed",
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            backgroundColor: "rgba(0,0,0,0.4)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1060,
          }}
        >
          <div className="modal-dialog" role="document">
            <div
              className="modal-content"
              style={{
                width: "40vw",
                borderRadius: "24px",
                padding: "24px",
                backgroundColor: "#FFF",
                border: "1px solid rgba(0, 0, 0, 0.175)",
              }}
            >
              <div
                className="modal-header"
                style={{
                  padding: "1rem 1rem",
                  borderBottom: "1px solid #dee2e6",
                  justifyContent: "space-between",
                  display: "flex",
                }}
              >
                <h5 className="modal-title main-title">SMS History Filter</h5>
                <HighlightOffIcon
                  onClick={handleClose}
                  style={{ cursor: "pointer" }}
                />
              </div>
              <Box component="form" onSubmit={handleSubmit(onSubmit)}>
                <div
                  className="scoped-modal modal-body"
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    width: "100%",
                  }}
                >
                  <div className="tab-container">
                    <ul className="tab-list">
                      {availableTabs.map((tab) => (
                        <div className="tab-wrapper" key={tab}>
                          <li
                            className={
                              selectedTab === tab
                                ? "tab-item selected"
                                : "tab-item"
                            }
                          >
                            <button
                              className={`tab-button ${
                                selectedTab === tab ? "active" : ""
                              }`}
                              onClick={(e) => handleTabChange(e, tab)}
                            >
                              {tab}
                            </button>
                          </li>
                          {selectedTab === tab && (
                            <img
                              src={ButtonTriangle}
                              alt="tri"
                              className="tab-triangle"
                            />
                          )}
                        </div>
                      ))}
                    </ul>

                    <div className="tab-content">
                      {selectedTab ===
                        (userRole === "CUST" ? "Company ID" : "Company Name") &&
                        !isCust && (
                          <div className="dateField">
                            <div className="dateInputWrapper">
                              <label className="dateLabel">
                                {userRole === "CUST"
                                  ? "Company ID"
                                  : "Company Name"}
                              </label>
                              {userRole === "CUST" ? (
                                <>
                                  <input
                                    type="text"
                                    placeholder="Enter Company ID"
                                    className="dateInput"
                                    {...register("userCode", {
                                      pattern: {
                                        value: /^[A-Za-z0-9]+$/,
                                        message:
                                          "Company ID can only contain letters and numbers",
                                      },
                                      maxLength: {
                                        value: 20,
                                        message:
                                          "Company ID cannot exceed 20 characters",
                                      },
                                    })}
                                    onChange={(e) => {
                                      const val = e.target.value.replace(
                                        /[^a-zA-Z0-9]/g,
                                        "",
                                      );
                                      setValue("userCode", val);
                                      setFilterData((prev) => ({
                                        ...prev,
                                        userCode: val,
                                      }));
                                    }}
                                  />
                                  {errors.userCode && (
                                    <span className="error-text">
                                      {errors.userCode.message}
                                    </span>
                                  )}
                                </>
                              ) : (
                                <>
                                  <div className="form-group position-relative">
                                    <select
                                      className="form-control select-dropdown"
                                      id="customerName"
                                      placeholder="Role"
                                      onClick={toggleCustomerNameDropdown}
                                      value={filterData.userCode || ""}
                                      onChange={(e) => {
                                        const selectedCode = e.target.value;
                                        const selectedCustomer =
                                          customerList.find(
                                            (customer) =>
                                              customer.customerCode ===
                                              selectedCode,
                                          );

                                        // Save customerCode for backend
                                        setFilterData((prev) => ({
                                          ...prev,
                                          userCode:
                                            selectedCustomer?.customerCode ||
                                            "",
                                          customerName:
                                            selectedCustomer?.name || "",
                                        }));

                                        // Optional: if using react-hook-form
                                        setValue(
                                          "userCode",
                                          selectedCustomer?.customerCode || "",
                                        );
                                        setValue(
                                          "customerName",
                                          selectedCustomer?.name || "",
                                        );
                                      }}
                                    >
                                      <option value="" disabled selected>
                                        Select Company Name
                                      </option>
                                      {customerList?.map((customer) => {
                                        return (
                                          <option
                                            key={customer.customerCode}
                                            value={customer.customerCode} // ✅ Pass customerCode as value
                                          >
                                            {customer.name}
                                          </option>
                                        );
                                      })}
                                    </select>
                                    <span className="position-absolute select-icon">
                                      {customerNameOpen ? (
                                        <ChevronUp />
                                      ) : (
                                        <ChevronDownIcon />
                                      )}
                                    </span>
                                  </div>
                                  {errors.customerName ? (
                                    <span className="error">
                                      {errors.customerName}
                                    </span>
                                  ) : null}

                                  {errors.customerName ? (
                                    <span className="error">
                                      {errors.customerName}
                                    </span>
                                  ) : null}
                                </>
                              )}
                            </div>
                          </div>
                        )}

                      {selectedTab === "Mobile No" && (
                        <div className="dateField">
                          <div className="dateInputWrapper">
                            <label className="dateLabel">Mobile No</label>
                            <Controller
                              name="mobileNo"
                              control={control}
                              rules={{
                                pattern: {
                                  value: /^[6-9]\d{9}$/,
                                  message:
                                    "Please enter a valid 10-digit mobile number starting with 6, 7, 8, or 9",
                                },
                                minLength: {
                                  value: 10,
                                  message:
                                    "Mobile No must be at least 10 digits",
                                },
                                validate: {
                                  noRepeats: (value) =>
                                    !value ||
                                    !/^([0-9])\1*$/.test(value) ||
                                    "Repeated digits are not allowed",
                                },
                              }}
                              render={({ field }) => (
                                <>
                                  <input
                                    {...field}
                                    type="text"
                                    placeholder="Enter Mobile No"
                                    className="dateInput"
                                    value={filterData.mobileNo || ""}
                                    onChange={(e) => {
                                      const val = e.target.value.replace(
                                        /[^0-9]/g,
                                        "",
                                      );
                                      setValue("mobileNo", val);
                                      setFilterData((prev) => ({
                                        ...prev,
                                        mobileNo: val,
                                      }));
                                      field.onChange(val);
                                    }}
                                    maxLength={15}
                                  />
                                  {errors.mobileNo && (
                                    <span className="error-text">
                                      {errors.mobileNo.message}
                                    </span>
                                  )}
                                </>
                              )}
                            />
                          </div>
                        </div>
                      )}

                      {selectedTab === "Status" && (
                        <div className="dateField">
                          <label
                            className="dateLabel"
                            style={{
                              marginBottom: "0.5rem",
                              fontWeight: "600",
                              fontSize: "25px",
                            }}
                          >
                            Status
                          </label>
                          <div
                            className="dateInputWrapper"
                            style={{
                              display: "flex",
                              gap: "1rem",
                              flexWrap: "wrap",
                              padding: "0.75rem",
                              backgroundColor: "#f8f9fa",
                              border: "1px solid #ced4da",
                              borderRadius: "0.5rem",
                            }}
                          >
                            {statusOptions.map((status) => (
                              <label
                                key={status.value}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "0.5rem",
                                  padding: "0.5rem 0.75rem",
                                  borderRadius: "0.375rem",
                                  cursor: "pointer",
                                  fontSize: "0.9rem",
                                  fontWeight: "500",
                                  boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={(filterData.status || []).includes(
                                    status.value,
                                  )}
                                  onChange={(e) => {
                                    const isChecked = e.target.checked;
                                    const newStatus = isChecked
                                      ? [
                                          ...(filterData.status || []),
                                          status.value,
                                        ]
                                      : (filterData.status || []).filter(
                                          (v) => v !== status.value,
                                        );
                                    setValue("status", newStatus);
                                    setFilterData((prev) => ({
                                      ...prev,
                                      status: newStatus,
                                    }));
                                  }}
                                  style={{ cursor: "pointer" }}
                                />
                                {status.label}
                              </label>
                            ))}
                          </div>
                        </div>
                      )}

                      {selectedTab === "Created Date" && (
                        <div
                          className="dateGrid"
                          style={{
                            display: "flex",
                            flexDirection: "row",
                            gap: "1rem",
                            alignItems: "flex-start",
                          }}
                        >
                          <div className="dateField" style={{ flex: 1 }}>
                            <label className="dateLabel">From Date</label>
                            <div className="dateInputWrapper">
                              <Controller
                                name="fromDate"
                                control={control}
                                rules={{
                                  validate: {
                                    validDate: (value) =>
                                      !value ||
                                      !isNaN(Date.parse(value)) ||
                                      "Invalid From Date",
                                    notFuture: (value) =>
                                      !value ||
                                      new Date(value) <= new Date() ||
                                      "From Date cannot be in the future",
                                    beforeToDate: (value) =>
                                      !value ||
                                      !filterData.toDate ||
                                      new Date(value) <=
                                        new Date(filterData.toDate) ||
                                      "From Date must be before To Date",
                                    requiredIfToDate: (value, formValues) =>
                                      !formValues.toDate ||
                                      !!value ||
                                      "From Date is required when To Date is selected",
                                  },
                                }}
                                render={({ field }) => (
                                  <>
                                    <input
                                      {...field}
                                      type="date"
                                      className="dateInput"
                                      value={filterData.fromDate || ""}
                                      onChange={(e) => {
                                        const val = e.target.value;
                                        setValue("fromDate", val);
                                        setFilterData((prev) => ({
                                          ...prev,
                                          fromDate: val,
                                        }));
                                        field.onChange(val);
                                      }}
                                      max={getTodayDate()}
                                    />
                                    {errors.fromDate && (
                                      <span className="error-text">
                                        {errors.fromDate.message}
                                      </span>
                                    )}
                                  </>
                                )}
                              />
                            </div>
                          </div>
                          <div className="dateField" style={{ flex: 1 }}>
                            <label className="dateLabel">To Date</label>
                            <div className="dateInputWrapper">
                              <Controller
                                name="toDate"
                                control={control}
                                rules={{
                                  validate: {
                                    validDate: (value) =>
                                      !value ||
                                      !isNaN(Date.parse(value)) ||
                                      "Invalid To Date",
                                    notFuture: (value) =>
                                      !value ||
                                      new Date(value) <= new Date() ||
                                      "To Date cannot be in the future",
                                    afterFromDate: (value) =>
                                      !value ||
                                      !filterData.fromDate ||
                                      new Date(value) >=
                                        new Date(filterData.fromDate) ||
                                      "To Date must be after From Date",
                                    requiredIfFromDate: (value, formValues) =>
                                      !formValues.fromDate ||
                                      !!value ||
                                      "To Date is required when From Date is selected",
                                  },
                                }}
                                render={({ field }) => (
                                  <>
                                    <input
                                      {...field}
                                      type="date"
                                      className="dateInput"
                                      value={filterData.toDate || ""}
                                      onChange={(e) => {
                                        const val = e.target.value;
                                        setValue("toDate", val);
                                        setFilterData((prev) => ({
                                          ...prev,
                                          toDate: val,
                                        }));
                                        field.onChange(val);
                                      }}
                                      max={getTodayDate()}
                                    />
                                    {errors.toDate && (
                                      <span className="error-text">
                                        {errors.toDate.message}
                                      </span>
                                    )}
                                  </>
                                )}
                              />
                            </div>
                          </div>
                        </div>
                      )}

                      {selectedTab === "Operator" && !isCust && (
                        <div className="dateField">
                          <label
                            className="dateLabel"
                            style={{
                              marginBottom: "0.5rem",
                              fontWeight: "600",
                              fontSize: "25px",
                            }}
                          >
                            Operator
                          </label>
                          <div
                            className="dateInputWrapper"
                            style={{
                              display: "flex",
                              gap: "1rem",
                              flexWrap: "wrap",
                              padding: "0.75rem",
                              backgroundColor: "#f8f9fa",
                              border: "1px solid #ced4da",
                              borderRadius: "0.5rem",
                            }}
                          >
                            {operatorOptions.map((operator) => (
                              <label
                                key={operator.value}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "0.5rem",
                                  padding: "0.5rem 0.75rem",
                                  borderRadius: "0.375rem",
                                  cursor: "pointer",
                                  fontSize: "0.9rem",
                                  fontWeight: "500",
                                  boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={(filterData.operator || []).includes(
                                    operator.value,
                                  )}
                                  onChange={(e) => {
                                    const isChecked = e.target.checked;
                                    const newOperator = isChecked
                                      ? [
                                          ...(filterData.operator || []),
                                          operator.value,
                                        ]
                                      : (filterData.operator || []).filter(
                                          (v) => v !== operator.value,
                                        );
                                    setValue("operator", newOperator);
                                    setFilterData((prev) => ({
                                      ...prev,
                                      operator: newOperator,
                                    }));
                                  }}
                                  style={{ cursor: "pointer" }}
                                />
                                {operator.label}
                              </label>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div
                  className="scoped-modal"
                  style={{
                    display: "flex",
                    justifyContent: "end",
                    padding: "12px",
                    paddingBottom: "0px",
                    gap: "16px",
                    borderTop: "1px solid #dee2e6",
                  }}
                >
                  <div className="buttonContainer">
                    <button
                      type="button"
                      className="btn btn-back"
                      onClick={() => {
                        reset({
                          userCode: "",
                          mobileNo: "",
                          status: [],
                          operator: [],
                          fromDate: getTodayDate(),
                          toDate: getTodayDate(),
                        });
                        setFilterData((prev) => ({
                          ...prev,
                          userCode: "", // ✅ clear the select
                          customerName: "",
                          fromDate: getTodayDate(),
                          toDate: getTodayDate(),
                        }));
                        handleReset();
                      }}
                    >
                      Reset
                    </button>
                  </div>
                  <div className="buttonContainer">
                    <button
                      type="submit"
                      className="btn btn-submit"
                      disabled={loading}
                    >
                      {loading ? (
                        <CircularProgress
                          style={{ width: "20px", height: "20px" }}
                        />
                      ) : (
                        "Submit"
                      )}
                    </button>
                  </div>
                </div>
              </Box>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SMSHistoryFilterModal;
